type CacheItem = {
  value: any;
  expiration: number;
};

type CacheConfig = {
  /** Default cache expiration time in milliseconds. */
  defaultExpirationMS?: number;
};

export default class CachedService {
  public static REGISTRATION = {
    name: 'cachedService',
    create: ({ configuration = {} }) => {
      return new CachedService({ configuration });
    },
  };

  private cache: Record<string, CacheItem> = {};
  private defaultExpirationMS: number;

  constructor({ configuration }: { configuration?: CacheConfig }) {
    this.defaultExpirationMS = configuration?.defaultExpirationMS || 3600000; // 1 hour default
  }

  /**
   * Stores a value in the cache with an optional expiration time.
   *
   * @param key - The key for the cache item.
   * @param value - The value to store in the cache.
   * @param expirationMS - Optional expiration time in milliseconds.
   */
  public set(key: string, value: any, expirationMS?: number): void {
    const expiration = Date.now() + (expirationMS || this.defaultExpirationMS);
    const cacheItem = { value, expiration };
    this.cache[key] = cacheItem; // Store in memory
    localStorage.setItem(key, JSON.stringify(cacheItem)); // Store in localStorage
  }

  /**
   * Retrieves a value from the cache.
   *
   * @param key - The key for the cache item.
   * @returns The cached value or undefined if the item is not found or expired.
   */
  public get(key: string): any {
    const cacheItem = this.cache[key] || JSON.parse(localStorage.getItem(key) || 'null');
    if (cacheItem) {
      if (Date.now() < cacheItem.expiration) {
        this.cache[key] = cacheItem; // Update memory cache
        return cacheItem.value;
      } else {
        // Item expired
        this.delete(key); // Remove from both memory and localStorage
      }
    }
    return undefined;
  }

  /**
   * Removes a value from the cache.
   *
   * @param key - The key for the cache item to remove.
   */
  public delete(key: string): void {
    delete this.cache[key];
    localStorage.removeItem(key); // Remove from localStorage
  }

  /**
   * Clears all cache items.
   */
  public clear(): void {
    this.cache = {};
    localStorage.clear(); // Clear localStorage
  }

  /**
   * Removes expired items from the cache.
   */
  public cleanExpired(): void {
    const now = Date.now();
    Object.keys(this.cache).forEach(key => {
      if (this.cache[key].expiration < now) {
        this.delete(key);
      }
    });
  }
}
